import { engine, type GameEvent } from './engine/engine';
import { useSession, useSettings } from './ui/store';

export const GA_MEASUREMENT_ID = 'G-V50XK2JZ7Z';
const ANALYTICS_NETWORK_DELAY_MS = 2500;
const ANALYTICS_SCRIPT_SELECTOR = 'script[data-bop-analytics="true"]';

type AnalyticsValue = string | number | boolean;
type AnalyticsParams = Record<string, AnalyticsValue | undefined>;
type GtagCommand = 'event' | 'config' | 'js';

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (command: GtagCommand, target: string | Date, params?: Record<string, unknown>) => void;
};

let initialized = false;
let lastLocation = '';
let lastNudges = useSession.getState().nudgesRemaining;
let previousSettings = {
  renderMode: useSettings.getState().renderMode,
  cameraMode: useSettings.getState().cameraMode,
  muted: useSettings.getState().muted,
  effectsEnabled: useSettings.getState().effectsEnabled,
  attractMotion: useSettings.getState().attractMotion,
};

function analyticsWindow(): AnalyticsWindow | null {
  if (typeof window === 'undefined') return null;
  return window as AnalyticsWindow;
}

function cleanParams(params: AnalyticsParams): Record<string, AnalyticsValue> {
  return Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, AnalyticsValue] => entry[1] !== undefined),
  );
}

export function trackEvent(name: string, params: AnalyticsParams = {}): void {
  const win = analyticsWindow();
  if (!win?.gtag) return;
  try {
    win.gtag('event', name, cleanParams(params));
  } catch {
    // Analytics is optional. A third-party script or browser extension that
    // replaces gtag with a throwing implementation must never break a
    // synchronous settings update, gameplay event, or UI interaction.
  }
}

function currentTableId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get('table') ?? undefined;
}

function trackVirtualPageView(): void {
  if (typeof window === 'undefined') return;
  const location = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (location === lastLocation) return;
  lastLocation = location;
  trackEvent('page_view', {
    page_location: window.location.href,
    page_path: location,
    table_id: currentTableId(),
  });
}

function patchHistory(): () => void {
  const push = window.history.pushState.bind(window.history);
  const replace = window.history.replaceState.bind(window.history);

  window.history.pushState = ((...args: Parameters<History['pushState']>) => {
    push(...args);
    trackVirtualPageView();
  }) as History['pushState'];
  window.history.replaceState = ((...args: Parameters<History['replaceState']>) => {
    replace(...args);
    trackVirtualPageView();
  }) as History['replaceState'];

  const onPopState = () => trackVirtualPageView();
  window.addEventListener('popstate', onPopState);
  return () => {
    window.history.pushState = push;
    window.history.replaceState = replace;
    window.removeEventListener('popstate', onPopState);
  };
}

function safeControlLabel(element: HTMLElement): string {
  const explicit = element.getAttribute('aria-label') || element.getAttribute('title');
  const raw = explicit || element.textContent || element.tagName.toLowerCase();
  return raw.replace(/\s+/g, ' ').trim().slice(0, 80);
}

function installUiTracking(): () => void {
  const onClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target.closest('button, a') : null;
    if (!(target instanceof HTMLElement)) return;
    const href = target instanceof HTMLAnchorElement ? target.getAttribute('href') ?? undefined : undefined;
    trackEvent('ui_click', {
      label: safeControlLabel(target),
      control: target.tagName.toLowerCase(),
      destination: href?.startsWith('/') ? href : undefined,
      table_id: currentTableId(),
    });
  };
  document.addEventListener('click', onClick, { capture: true });
  return () => document.removeEventListener('click', onClick, { capture: true });
}

function installGameplayTracking(): () => void {
  const importantEvents = new Set<GameEvent['type']>([
    'game-start',
    'game-over',
    'jackpot',
    'ramp',
    'target-activated',
  ]);
  return engine.onEvent((event) => {
    if (!importantEvents.has(event.type)) return;
    trackEvent('gameplay_event', {
      event_type: event.type,
      table_id: currentTableId(),
    });
  });
}

function installSessionTracking(): () => void {
  return useSession.subscribe((state) => {
    if (state.nudgesRemaining < lastNudges) {
      trackEvent('nudge_used', {
        remaining: state.nudgesRemaining,
        table_id: currentTableId(),
      });
    } else if (state.nudgesRemaining > lastNudges) {
      trackEvent('nudge_recharged', {
        available: state.nudgesRemaining,
        table_id: currentTableId(),
      });
    }
    lastNudges = state.nudgesRemaining;
  });
}

function installSettingsTracking(): () => void {
  return useSettings.subscribe((state) => {
    if (state.renderMode !== previousSettings.renderMode) {
      trackEvent('view_changed', { render_mode: state.renderMode, table_id: currentTableId() });
    }
    if (state.cameraMode !== previousSettings.cameraMode) {
      trackEvent('camera_changed', { camera_mode: state.cameraMode, table_id: currentTableId() });
    }
    if (state.muted !== previousSettings.muted) {
      trackEvent('sound_changed', { muted: state.muted });
    }
    if (state.effectsEnabled !== previousSettings.effectsEnabled) {
      trackEvent('effects_changed', { enabled: state.effectsEnabled });
    }
    if (state.attractMotion !== previousSettings.attractMotion) {
      trackEvent('attract_motion_changed', { enabled: state.attractMotion });
    }
    previousSettings = {
      renderMode: state.renderMode,
      cameraMode: state.cameraMode,
      muted: state.muted,
      effectsEnabled: state.effectsEnabled,
      attractMotion: state.attractMotion,
    };
  });
}

export interface AnalyticsScriptEnvironment {
  window: AnalyticsWindow;
  document: Pick<Document, 'createElement' | 'querySelector'> & {
    head: Pick<HTMLHeadElement, 'appendChild'>;
  };
}

export function deferGoogleAnalyticsNetwork(
  environment?: AnalyticsScriptEnvironment,
  delayMs = ANALYTICS_NETWORK_DELAY_MS,
): () => void {
  const win = environment?.window ?? analyticsWindow();
  const doc = environment?.document ?? (typeof document === 'undefined' ? null : document);
  if (!win || !doc) return () => undefined;

  win.dataLayer ??= [];
  if (!win.gtag) {
    win.gtag = (command, target, params) => {
      win.dataLayer?.push(params === undefined ? [command, target] : [command, target, params]);
    };
  }

  try {
    win.gtag('js', new Date());
    win.gtag('config', GA_MEASUREMENT_ID);
  } catch {
    // Keep third-party analytics entirely optional even if a browser extension
    // replaces the queue shim before the deferred script is requested.
  }

  let cancelled = false;
  let idleId: number | undefined;
  let delayId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const loadScript = () => {
    if (cancelled || doc.querySelector(ANALYTICS_SCRIPT_SELECTOR)) return;
    try {
      const script = doc.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      script.dataset.bopAnalytics = 'true';
      if ('fetchPriority' in script) script.fetchPriority = 'low';
      doc.head.appendChild(script);
    } catch {
      // A CSP, extension, DOM shim, or transient browser state may reject script
      // insertion. Analytics must never become an application dependency.
    }
  };

  delayId = globalThis.setTimeout(() => {
    delayId = undefined;
    const requestIdle = win.requestIdleCallback?.bind(win);
    if (requestIdle) idleId = requestIdle(loadScript, { timeout: 2000 });
    else loadScript();
  }, Math.max(0, delayMs));

  return () => {
    cancelled = true;
    if (delayId !== undefined) globalThis.clearTimeout(delayId);
    delayId = undefined;
    if (idleId !== undefined) win.cancelIdleCallback?.(idleId);
    idleId = undefined;
  };
}

export function initAnalytics(): () => void {
  if (initialized || typeof window === 'undefined') return () => undefined;
  initialized = true;
  lastLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  const cleanups = [
    deferGoogleAnalyticsNetwork(),
    patchHistory(),
    installUiTracking(),
    installGameplayTracking(),
    installSessionTracking(),
    installSettingsTracking(),
  ];

  trackEvent('app_ready', {
    table_id: currentTableId(),
    render_mode: useSettings.getState().renderMode,
  });

  return () => cleanups.forEach((cleanup) => cleanup());
}
